import { DocumentContentsAccess, GeneralDocumentAccess } from "./resources/documents/documents-permissions-resources";
import type { Resource } from "./resources/permission-resource";
import { NotificationsAccess } from "./resources/notifications/notifications-permissions-resources";
import { LoginAccess, UserNetworkingAccess } from "./resources/users/users-permissions-resources";
import {
  ContractsAccess,
  CurrencyFieldsAccess,
  DiscountsAccess,
  FeeSchedulesAccess,
  InvoicesAccess,
  ProposalsAccess,
  TeamsAccess,
  WorkAgreementsAccess,
} from "./resources/work/work-permissions-resources";
import Guid from "_shared/guids/guid";
import { LegalEntityAccess } from "permissions/resources/legal-entities/legal-entities-permissions-resources";
import { CommunicationsAccess } from "permissions/resources/messaging/messaging-permissions-resources";
import { MarketplaceAccess, MarketplaceDirectoryAccess, MarketplaceSearchAccess } from "permissions/resources/marketplace/marketplace-permissions-resources";
import { KnowledgeBaseAccess } from "permissions/resources/knowledge-base/knowledge-base-permissions-resources";
import AHDate from "_shared/dates/date";
import PermissionSchema, { PermissionJSON } from "permissions/_schemas/permission-schema";
import { PermissionCtorParams } from "permissions/_views/permission-table-view-model";
import { resourceFromJSON } from "permissions/resources/permission-resource-factory";

export default class Permission {
  public static fromJSON(permissionJSON: PermissionJSON): Permission {
    const parsed = PermissionSchema.deserialize(permissionJSON);

    const resource = resourceFromJSON(permissionJSON.resource);
    const parentPermissions = permissionJSON.parentPermissions
      ? permissionJSON.parentPermissions.map((p) => Permission.fromJSON(p))
      : undefined;

    const ctorParams: PermissionCtorParams = {
      ...parsed,
      resource,
      parentPermissions,
    };

    return new Permission(ctorParams);
  }

  public static fromJSONArray(jsonArray: PermissionJSON[]): Permission[] {
    return jsonArray.map((j) => Permission.fromJSON(j));
  }

  public static topLevelResources(): Resource[] {
    return [
      ...LoginAccess.getPermutations(),
      ...LegalEntityAccess.getPermutations(),
      ...UserNetworkingAccess.getPermutations(),
      ...CommunicationsAccess.getPermutations(),
      ...NotificationsAccess.getPermutations(),
      ...GeneralDocumentAccess.getPermutations(),
      ...DocumentContentsAccess.getPermutations(),
      ...MarketplaceAccess.getPermutations(),
      ...MarketplaceSearchAccess.getPermutations(),
      ...MarketplaceDirectoryAccess.getPermutations(),
      ...WorkAgreementsAccess.getPermutations(),
      ...ProposalsAccess.getPermutations(),
      ...TeamsAccess.getPermutations(),
      ...ContractsAccess.getPermutations(),
      ...FeeSchedulesAccess.getPermutations(),
      ...CurrencyFieldsAccess.getPermutations(),
      ...DiscountsAccess.getPermutations(),
      ...InvoicesAccess.getPermutations(),
      ...KnowledgeBaseAccess.getPermutations(),
    ];
  }

  public readonly id?: Guid;
  public readonly resource: Resource;
  public readonly grantorUserId: Guid;
  public readonly granteeUserId: Guid;
  public readonly entityId: Guid;
  public readonly disabledDate?: AHDate;
  public readonly createdDate?: AHDate;
  public readonly parentPermissions?: Permission[];
  public readonly note?: string;
  public readonly version: number;

  public get isDisabled(): boolean {
    return !!this.disabledDate;
  }

  public get isVirtual(): boolean {
    return !this.id;
  }

  public constructor(params: PermissionCtorParams) {
    this.id = params.id;
    this.resource = params.resource;
    this.granteeUserId = params.granteeUserId;
    this.grantorUserId = params.grantorUserId;
    this.entityId = params.entityId;
    this.disabledDate = params.disabledDate;
    this.createdDate = params.createdDate;
    this.parentPermissions = params.parentPermissions;
    this.note = params.note;
    this.version = params.version ?? 0;
  }

  public toJSON(): PermissionJSON {
    const json: PermissionJSON = {
      id: this.id?.toJSON(),
      resource: this.resource.toJSON() as PermissionJSON["resource"],
      entityId: this.entityId.toJSON(),
      grantorUserId: this.grantorUserId.toJSON(),
      granteeUserId: this.granteeUserId.toJSON(),
      disabledDate: this.disabledDate?.toJSON(),
      createdDate: this.createdDate?.toJSON(),
      parentPermissions: this.parentPermissions?.map((p) => p.toJSON()),
      note: this.note,
      version: this.version,
    };

    return PermissionSchema.validateJSON(json);
  }

  public isEqualTo(other: Permission): boolean {
    if (!other) return false;
    const idsEqual = Boolean((!this.id && !other.id) || (this.id && other.id && this.id.isEqualTo(other.id)));
    return (
      idsEqual &&
      this.resource.resourceType === other.resource.resourceType &&
      this.granteeUserId.isEqualTo(other.granteeUserId) &&
      this.grantorUserId.isEqualTo(other.grantorUserId)
    );
  }

  public toString(): string {
    return `Permission { 
      id: ${this.id?.value ?? "undefined"}, 
      resource: ${this.resource.resourceType}, 
      entityId: ${this.entityId.value},
      granteeUserId: ${this.granteeUserId.value}, 
      grantorUserId: ${this.grantorUserId.value},
      disabledDate: ${this.disabledDate?.toString() ?? "undefined"},
      createdDate: ${this.createdDate?.toString() ?? "undefined"},
      parentPermissions: ${this.parentPermissions?.map((p) => p.toString()).join(", ") ?? "undefined"},
      note: ${this.note ?? "undefined"}
    }`;
  }
}
